import {
  BadRequestError,
  MissingArgumentsError,
  NetworkError,
  NotFoundError,
  SSError,
  UnauthorizedRequestError,
  isError,
} from "data/ss-error";
import {
  type ClaimConstants,
  type ClaimList,
  type DecodeData,
  type PaymentPlanDetails,
} from "gql/__generated__/hooks";
import { type TingEnrollmentValues } from "pages/offer/ting/hooks";
import { type WindDefenseEnrollmentValues } from "pages/offer/wind-defense/hooks";
import { type EasyPayFormValues } from "pages/payment-plan-update/payment-plan-update";

import {
  type KeystoneClaimsData,
  type KeystoneDocusignStatus,
  type KeystoneInspections,
  type KeystoneMortgageeInfo,
  type KeystoneOfferEligibility,
  type KeystonePaymentInfo,
  type KeystonePolicyDetail,
  type KeystonePolicyInfo,
  type PolicyDetail,
  type PropertySection,
  type PropertyUpdateItem,
  claimsDataMap,
  policyDetailMap,
} from "../utils/keystone-api-mapping";
import { captureException } from "../utils/sentry";

type QueryParams = Record<string, string>;

export type KeystoneResult<T> = T | SSError;

type KeystoneSuccess = { success: boolean };

type PaymentsPlans = {
  policyPaymentPlans: PaymentPlanDetails[];
  easypayProcessingFees: string;
};

type KeystoneInsuredInfoUpdate = {
  phoneNumber?: string;
  emailAddress?: string;
  mailingAddress?: {
    streetLine1: string;
    streetLine2?: string;
    city: string;
    state: string;
    zipFirstFive: string;
    mailingAddressUpdateReason: string;
    mailingAddressUpdateDetails: string;
  };
};

type CoverageData = {
  coverages: CoverageAvailibility[];
  policyRenewal: {
    startDate: string;
    endDate: string;
  };
  premiumCurrent: number;
};

type CoverageAvailibility = {
  name: "IDENTITY_FRAUD" | "MECHANICAL_BREAKDOWN" | "SERVICE_LINE";
  protectionValue: number;
  eligibility: boolean;
  startDate: string;
};

type Coverage = {
  name: string;
  protectionValue?: number;
  eligibility?: boolean;
  price?: number;
  startDate?: string;
  proratedPrice?: number;
  premiumNew?: number;
};

type CoverageUpsell = {
  coverage: Coverage;
  policyRenewal: CoverageData["policyRenewal"];
  premiumPrevious: number;
};

export type CoveragePreview = {
  coverages: Coverage[];
  policyRenewal: CoverageData["policyRenewal"];
  premiumCurrent: number;
};

const UNAUTH_ERR_MSG =
  "no auth token provided to a private keystone-api request";
const NO_EMAIL_ERR_MSG = "no user email provided";
const NO_USER_ID_MSG = "no user id provided";
const NO_POLICY_ID_ERR_MSG = "no policy ID provided";
const NO_FNOL_EVENT_ERR_MSG = "no FNOL event provided";
const NO_ZIP_CODE_ERR_MSG = "no ZIP Code provided";
const NO_ENVELOPE_ID_ERR_MSG = "no envelope ID provided";
const NO_DATA_ERR_MSG = "no encoded data provided";
const NO_PAYMENT_INFO_MSG = "no payment info provided";
const NO_ACCOUNT_PREFERENCES_MSG = "no account preferences provided";

/**
 * Keystone API
 */
export class KeystoneApiClient {
  private readonly baseUrl: string;
  protected token?: string;

  /**
   * Setup a keystone-api client to make direct requests to the backend.
   * - provide the `baseUrl`` for the appropriate environment, like https://stage-sagesure-svc.icg360.org/cru-4/keystone
   * - optionally, provide a `token` to avoid using Auth0, probably only useful in tests
   */
  constructor(baseUrl: string, token?: string) {
    this.baseUrl = baseUrl;
    this.token = token;
  }

  /**
   * Internal implementation for GET requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - if a token is provided it will be sent as an auth header
   * - an optional query string will be constructed as needed
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  protected readonly get = async <T>(
    endpoint: string,
    token?: string,
    query?: QueryParams
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}/${endpoint}`);
    if (query) {
      Object.keys(query).forEach((key) => {
        url.searchParams.append(key, query[key]);
      });
    }
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, { headers });
      const parsedRes = await parseResponse<T>(response);
      if (isError(parsedRes)) {
        captureException(parsedRes);
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for POST requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - if a token is provided it will be sent as an auth header
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly post = async <T>(
    endpoint: string | URL,
    token?: string,
    body?: BodyInit,
    headers: HeadersInit = {},
    emptyRes = false
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}/${endpoint}`);
    const postHeaders = headers;
    if (token) {
      postHeaders["Authorization"] = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, {
        headers,
        method: "POST",
        body,
      });
      const parsedRes = await parseResponse<T>(response, emptyRes);
      if (isError(parsedRes)) {
        captureException(parsedRes);
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for PUT requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - if a token is provided it will be sent as an auth header
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly put = async <T>(
    endpoint: string | URL,
    token?: string,
    body?: BodyInit,
    headers: HeadersInit = {}
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}/${endpoint}`);
    const postHeaders = headers;
    if (token) {
      postHeaders["Authorization"] = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, {
        headers,
        method: "PUT",
        body,
      });
      const parsedRes = await parseResponse<T>(response);
      if (isError(parsedRes)) {
        captureException(parsedRes);
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Internal implementation for PATCH requests to Keystone API.
   * - endpoint is a relative path applied to the API base url
   * - if a token is provided it will be sent as an auth header
   * - optional body is sent as-is
   * - all errors are handled, api consumers shouldn't need to catch exceptions
   */
  private readonly patch = async <T>(
    endpoint: string | URL,
    token?: string,
    body?: BodyInit,
    headers: HeadersInit = {}
  ): Promise<KeystoneResult<T>> => {
    const url = new URL(`${this.baseUrl}/${endpoint}`);
    const postHeaders = headers;
    if (token) {
      postHeaders["Authorization"] = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, {
        body,
        headers,
        method: "PATCH",
      });
      const parsedRes = await parseResponse<T>(response);
      if (isError(parsedRes)) {
        captureException(parsedRes);
      }
      return parsedRes;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  private readonly delete = async (
    endpoint: string | URL,
    token?: string,
    headers: HeadersInit = {}
  ): Promise<KeystoneResult<null>> => {
    const url = new URL(`${this.baseUrl}/${endpoint}`);
    const postHeaders = headers;
    if (token) {
      postHeaders["Authorization"] = `Bearer ${token}`;
    }
    try {
      const response = await fetch(url, {
        headers,
        method: "DELETE",
      });
      if (!response.ok) {
        const err = new KeystoneServerError("delete failed", response.status);
        captureException(err);
        return err;
      }
      return null;
    } catch (err) {
      captureException(err);
      return new NetworkError(err.message);
    }
  };

  /**
   * Set token to be used in authenticated requests to keystone api.
   *
   * TODO: This will go away in favor of the keystone-access cookie set server-side.
   */
  setToken = (token: string) => {
    this.token = token;
  };

  /**
   * Decode an encrypted string of user data
   */
  decodeData = async (
    dataString: string
  ): Promise<KeystoneResult<DecodeData>> => {
    if (!dataString) {
      return new MissingArgumentsError(NO_DATA_ERR_MSG);
    }
    const url = new URL(`${this.baseUrl}/api/rest/v1/data/decode`);
    return await this.post<DecodeData>(url, "", dataString);
  };

  /**
   * Fetch a keystone user by email.
   */
  getUser = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const res = await this.get<KeystoneUserResponse>(
      "api/rest/v1/user/email",
      this.token,
      {
        email,
      }
    );
    if (isError(res)) {
      return res;
    }
    const user: KeystoneUser = {
      ...res,
      policyList: res.policyList.map((p) => {
        return {
          keystonePolicyId: p.keystonePolicyId,
          quoteNumber: p.quoteNumber,
          platformPolicyId: p.platformPolicyId,
          policyState: p.policyState,
          carrier: p.carrier,
          carrierId: p.carrierId,
          carrierGroupId: p.carrierGroupId,
          isCommercial: !!p.isCommercial,
          propertyAddress: p.addresses?.find(
            (addr) => addr.addressType === "PROPERTY"
          ),
          agencyLocationCode: p.terms[0]?.agencyLocationCode ?? "",
        };
      }),
    };
    return user;
  };

  /**
   * Link a policy to keystone user. Error message is mapped for display.
   */
  linkPolicy = async (email: string, policyNum: string, zip: string) => {
    if (!email || !policyNum || !zip) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);

    const params = new URLSearchParams();
    params.append("zipCode", zip);
    const res = await this.patch<void>(
      `api/rest/v1/user/${encodedEmail}/policy/${policyNum}?${params.toString()}`,
      this.token,
      undefined,
      { "Content-Type": "application/x-www-form-urlencoded" }
    );

    if (res instanceof SSError) {
      res.message =
        "Sorry, something went wrong while adding your policy. Please try again in a few minutes or call us at (800) 481-0661.";
      if (res instanceof NotFoundError) {
        res.message =
          "We were unable to find a SageSure insurance policy with that combination of policy number and zip code. Please try again.";
      } else if (res instanceof KeystoneServerError && res.statusCode === 409) {
        res.message = "This policy is already linked to your account.";
      }
      return res;
    }

    return res;
  };

  /**
   * Remove a linked policy from keystone user.
   */
  removePolicy = async (email: string, policyNum: string) => {
    if (!email || !policyNum) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);
    return this.delete(
      `api/rest/v1/user/${encodedEmail}/policy/${policyNum}`,
      this.token
    );
  };

  /**
   * Fetch a user's policy list by email.
   */
  getPoliciesList = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const encodedEmail = encodeURIComponent(email);
    return this.get<KeystonePolicyInfo[]>(
      `api/rest/v1/user/${encodedEmail}/policyList`,
      this.token
    );
  };

  /**
   * Request password reset email
   */
  requestPasswordReset = async (email: string) => {
    if (!email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    const params = new URLSearchParams();
    params.append("email", email);
    return await this.post<null>(
      `api/rest/v1/user/password/reset?${params.toString()}`,
      undefined,
      undefined,
      undefined,
      true
    );
  };

  /**
   * Fetch a policy's claim list
   */
  getClaimsList = async (
    policyId: string
  ): Promise<KeystoneResult<ClaimList>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }

    const encodedPolicyId = encodeURIComponent(policyId);
    const response = await this.get<KeystoneClaimsData>(
      `api/rest/v1/claims/${encodedPolicyId}?lossFlag=true`,
      this.token
    );

    if (response instanceof SSError) {
      return response;
    }

    return claimsDataMap(response);
  };

  /**
   * Fetch policy details
   */
  getPolicy = async (
    policyId: string
  ): Promise<KeystoneResult<PolicyDetail>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const apiRes = await this.get<KeystonePolicyDetail>(
      `api/rest/v1/policy/${policyId}`,
      this.token
    );
    if (apiRes instanceof SSError) {
      return apiRes;
    }
    return policyDetailMap(apiRes);
  };

  /**
   * Fetch a policy's claim constants
   */
  getClaimConstants = async (
    policyId: string
  ): Promise<KeystoneResult<ClaimConstants>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const apiRes = await this.get<ClaimConstants>(
      `api/rest/v1/claims/constants/${policyId}`,
      this.token
    );

    return apiRes;
  };

  /**
   * Fetch a user's UI events
   */
  getUiEvents = async (userId: string): Promise<KeystoneResult<UiEvents>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    return await this.get<UiEvents>(
      `api/rest/v1/ui-events/${userId}`,
      this.token
    );
  };

  /**
   * Fetch a policy's featured offers
   */
  getPolicyOffers = async (
    keystonePolicyId: string,
    featured?: boolean
  ): Promise<KeystoneResult<KeystoneOfferEligibility[]>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!keystonePolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const res = await this.get<KeystoneOfferEligibility[]>(
      `api/rest/v1/offers/${keystonePolicyId}${
        featured ? "?featured=true" : ""
      }`,
      this.token
    );

    return res;
  };

  /**
   * Fetch payment plans for policy along with easypay processing fees
   */
  getPaymentPlans = async (
    policyId: string
  ): Promise<KeystoneResult<PaymentsPlans>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    const apiRes = await this.get<{ data: PaymentsPlans }>(
      `api/rest/v1/paymentplan/${policyId}`,
      this.token
    );
    if (isError(apiRes)) {
      return apiRes;
    }
    return apiRes.data;
  };

  /**
   * Fetch a policy's inspections data
   */
  getPolicyInspections = async (
    policyId: string
  ): Promise<KeystoneResult<KeystoneInspections>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const apiRes = await this.get<KeystoneInspections>(
      `api/rest/v1/inspection/${policyId}?uwActionCodeFilter=WRITE_OUT&resolvedFilter=FALSE&groupRoofConditions=TRUE`,
      this.token
    );

    return apiRes;
  };

  /**
   * Submit an FNOL (First Notice Of Loss) event.
   */
  fnolChannelSubmit = async (event: unknown) => {
    if (!event) {
      return new MissingArgumentsError(NO_FNOL_EVENT_ERR_MSG);
    }
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }

    return this.post("/api/rest/v1/channel", this.token, JSON.stringify(event));
  };

  /**
   * Create an offer for a policy.
   */
  createOffer = async (
    keystonePolicyId: string,
    offerInternalName: string
  ): Promise<KeystoneResult<KeystoneOfferEligibility>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!keystonePolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    return this.post(
      `/api/rest/v1/offer/${keystonePolicyId}/offering/${offerInternalName}`,
      this.token
    );
  };

  verifyPayment = async (
    policyId: string,
    zip: string
  ): Promise<KeystoneResult<KeystonePaymentInfo>> => {
    if (!policyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    if (!zip) {
      return new MissingArgumentsError(NO_ZIP_CODE_ERR_MSG);
    }
    const params = new URLSearchParams();
    params.append("policyId", policyId);
    params.append("zipcode", zip);
    return this.post<KeystonePaymentInfo>(
      `api/rest/v1/paymentinfo/verify?${params.toString()}`
    );
  };

  enrollEasypay = async (
    paymentInfo: EasyPayFormValues,
    user: { firstName: string; lastName: string; email: string }
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!paymentInfo || !user) {
      return new MissingArgumentsError(NO_PAYMENT_INFO_MSG);
    }
    const formData = new FormData();
    formData.append("paymentFormData", JSON.stringify(paymentInfo));
    formData.append("sessionData", JSON.stringify({ user }));
    return this.post<KeystoneSuccess>(
      `api/rest/v1/easypay/enroll`,
      this.token,
      formData
    );
  };

  /**
   * Enroll a policy in wind defense program offer (shingles).
   */
  enrollWindDefense = async (
    enrollWindDefenseInfo: WindDefenseEnrollmentValues
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    return this.post<KeystoneSuccess>(
      `api/rest/v1/homeservices/order/winddefense/`,
      this.token,
      JSON.stringify(enrollWindDefenseInfo),
      headers
    );
  };

  /**
   * Enroll a policy in Ting program offer (electrical fire detector).
   */
  enrollTing = async (
    enrollTingInfo: TingEnrollmentValues
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    return this.post<KeystoneSuccess>(
      `api/rest/v1/homeservices/order/ting/`,
      this.token,
      JSON.stringify(enrollTingInfo),
      headers
    );
  };

  getDocumentsToSign = async (
    insightPolicyId: string
  ): Promise<KeystoneResult<KeystoneDocusignStatus[]>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!insightPolicyId) {
      return new MissingArgumentsError(NO_POLICY_ID_ERR_MSG);
    }
    return this.get<KeystoneDocusignStatus[]>(
      `api/rest/v1/docusign/status/${insightPolicyId}`,
      this.token
    );
  };

  /**
   * Get a signing link for a document by envelope ID
   */
  getDocumentSigningUrl = async (
    envelopeId: string
  ): Promise<KeystoneResult<{ signingLink: string }>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!envelopeId) {
      return new MissingArgumentsError(NO_ENVELOPE_ID_ERR_MSG);
    }
    return this.get<{ signingLink: string }>(
      `api/rest/v1/docusign/signinglink/${envelopeId}`,
      this.token
    );
  };

  /**
   * Update user's account preference key/value store
   */
  updateAccountPreferences = async (
    accountPreferences: { preferenceName: string; preferenceValue: string },
    user: { userId: string; email: string }
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!user?.email) {
      return new MissingArgumentsError(NO_EMAIL_ERR_MSG);
    }
    if (!user?.userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }
    if (!accountPreferences) {
      return new MissingArgumentsError(NO_ACCOUNT_PREFERENCES_MSG);
    }
    const requestBody = {
      userId: user.userId,
      userEmail: user.email,
      preferenceName: accountPreferences.preferenceName,
      preferenceValue: accountPreferences.preferenceValue,
    };
    return this.post<KeystoneSuccess>(
      `api/rest/v1/preferences/account`,
      this.token,
      JSON.stringify(requestBody),
      { "content-type": "application/json" }
    );
  };

  updateMarketingUiEvent = async (
    userId: string,
    currentCount: number
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    if (!userId) {
      return new MissingArgumentsError(NO_USER_ID_MSG);
    }

    const requestBody = {
      marketingOptInPresented: {
        count: currentCount + 1,
        presentedAt: new Date(),
      },
    };
    return this.patch<KeystoneSuccess>(
      `api/rest/v1/ui-events/${userId}`,
      this.token,
      JSON.stringify(requestBody)
    );
  };

  /**
   * Update policy's property data; roof, trampoline, fire + security alarms, and pool.
   */
  updateProperty = async (
    request: {
      insightPolicyId?: string;
      userEmail?: string;
      propertySection: PropertySection;
      propertyUpdateItems: PropertyUpdateItem[];
    },
    files?: File[]
  ): Promise<KeystoneResult<KeystoneSuccess>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const formData = new FormData();
    formData.append("request", JSON.stringify(request));
    if (files?.length) {
      for (const file of files) {
        formData.append("file", file);
      }
    }
    return this.post<KeystoneSuccess>(
      `api/rest/v1/property/update`,
      this.token,
      formData
    );
  };

  registerUser = (user: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    policyListCsv: string;
    zipCode: string;
  }): Promise<KeystoneResult<KeystoneUser>> => {
    return this.post<KeystoneUser>(
      `api/rest/v1/user/register`,
      undefined,
      JSON.stringify(user),
      { "content-type": "application/json" }
    );
  };

  mortgageeSearch = (searchTerm: string) => {
    const encodedSearchTerm = encodeURIComponent(searchTerm.trim());
    return this.get<KeystoneMortgageeInfo[]>(
      `api/rest/v1/mortgagee/lookup/${encodedSearchTerm}`,
      this.token
    );
  };

  updateMortgagee = ({ mortgagee, keystonePolicyId }) => {
    return this.put<null>(
      `api/rest/v1/mortgagee/${keystonePolicyId}`,
      this.token,
      JSON.stringify(mortgagee),
      { "content-type": "application/json" }
    );
  };

  sendEmailVerification = (email: string): Promise<KeystoneResult<void>> => {
    const params = new URLSearchParams();
    params.append("emailaddress", email);
    return this.post<void>(`api/rest/v1/user/reverify?${params.toString()}`);
  };

  /**
   * Update insured's email, phone, or mailing address.
   */
  updateInsuredInfo = (
    policyId: string,
    email: string,
    username: string,
    update: {
      phone?: string;
      email?: string;
      mailingAddress?: {
        streetLine1: string;
        streetLine2?: string;
        city: string;
        state: string;
        zipFirstFive: string;
        mailingAddressUpdateReason: string;
        mailingAddressUpdateDetails: string;
      };
    }
  ): Promise<KeystoneResult<void>> => {
    const updateables: KeystoneInsuredInfoUpdate = {};
    if (update.phone) {
      updateables.phoneNumber = update.phone;
    }
    if (update.email) {
      updateables.emailAddress = update.email;
    }
    if (update.mailingAddress) {
      updateables.mailingAddress = update.mailingAddress;
    }
    const formData = new FormData();
    formData.append("policyId", policyId);
    formData.append("emailAddress", email);
    formData.append("username", username);
    formData.append("updateables", JSON.stringify(updateables));
    return this.put<void>(
      `api/rest/v1/property/profile/insuredinfo`,
      this.token,
      formData
    );
  };

  /**
   * Enroll a policy in paperless billing.
   */
  enrollPaperlessBilling = (
    policyId: string
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `api/rest/v1/paperlessbilling/enroll/${policyId}`,
      this.token,
      undefined,
      { "content-type": "application/json" }
    );
  };

  /**
   * Update a user's paperless billing email address for a specific policy.
   */
  updatePaperlessBillingEmail = (
    policyId: string,
    email: string
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `api/rest/v1/paperlessbilling/billing-email-address/${policyId}`,
      this.token,
      JSON.stringify({ opInsuredEmailAddressBilling: email }),
      { "content-type": "application/json" }
    );
  };

  /**
   * Update a user's paperless billing email address for a specific policy.
   */
  updatePaperlessDocsEmail = (
    policyId: string,
    email: string
  ): Promise<KeystoneResult<void>> => {
    return this.patch<void>(
      `api/rest/v1/paperlessdocuments/documents-email-address/${policyId}`,
      this.token,
      JSON.stringify({ opInsuredEmailAddressDocuments: email }),
      { "content-type": "application/json" }
    );
  };

  /**
   * Get a list of coverages available for a given policy.
   */
  eligibleCoverages = (
    policyNum: string
  ): Promise<KeystoneResult<CoverageData>> => {
    return this.get<CoverageData>(
      `api/rest/v1/coverages/eligible/${policyNum}`,
      this.token
    );
  };

  /**
   * Get prices for a coverage
   */
  coveragePreview = (
    policyNum: string,
    coverageName: string,
    protectionValue: number
  ): Promise<KeystoneResult<CoveragePreview>> => {
    const params = new URLSearchParams();
    params.append("coverageName", coverageName);
    params.append("protectionValue", protectionValue.toString());
    return this.get<CoveragePreview>(
      `api/rest/v1/coverages/preview/${policyNum}?${params.toString()}`,
      this.token
    );
  };

  /**
   * Purchase additional coverage.
   */
  purchaseCoverage = async ({
    policyNum,
    coverageName,
    protectionValue,
  }: {
    policyNum: string;
    coverageName: string;
    protectionValue: number;
  }): Promise<KeystoneResult<CoverageUpsell>> => {
    if (!this.token) {
      return new UnauthorizedRequestError(UNAUTH_ERR_MSG);
    }
    const params = new URLSearchParams();
    params.append("policyId", policyNum);
    params.append("coverageName", coverageName);
    params.append("protectionValue", protectionValue.toString());
    return this.post<CoverageUpsell>(
      `api/rest/v1/coverages/purchase?${params.toString()}`,
      this.token
    );
  };
}

/**
 * Used in client to parse api responses.
 */
async function parseResponse<T>(
  res: Response,
  empty = false
): Promise<KeystoneResult<T>> {
  if (res.status === 204 || (res.status === 200 && empty)) {
    return null as T;
  }

  const body = await res.json();
  if (!res.ok) {
    const message = body.message || res.statusText;
    if (res.status === 400) {
      return new BadRequestError(message || "bad request data");
    } else if (res.status === 404) {
      return new NotFoundError(message || "not found");
    } else if (res.status === 401 || res.status === 403) {
      return new UnauthorizedRequestError(message || "unauthorized");
    }
    return new KeystoneServerError(
      message || "internal server error",
      res.status
    );
  }
  return body as T;
}

/**
 * Internal backend server error.
 */
class KeystoneServerError extends SSError {
  statusCode: number;
  constructor(message: string, statusCode: number = 500) {
    super(message);
    this.name = "KeystoneServerError";
    this.cause = "Internal Keystone API error";
    this.statusCode = statusCode;
  }
}
